import {useCallback, useRef, useState} from 'react'
import {EVENT_ANALYTICS_TYPE_UPDATED, trackUpdate} from '../../../../utils/matomoEvents'
import {ADVANCED_ANALYTICS} from "../../../../types/analytics"
import {DEFAULT_ERROR_MESSAGE} from '../../../../api/Api'
import useGetAdvancedAnalyticsMutation from "../../../../queries/useGetAdvancedAnalyticsMutation";
import {IAdvancedAnalyticsApiPayload} from "../../../../types/chart";

export const useAdvancedAnalytics = (coordinates: number[], isFwe?: boolean, fwePointName?: string) => {
  const [images, setImages] = useState({
    statistics: '',
    extremeValues: '',
    persistence: '',
    histogram: '',
    exceedance: '',
    tidal: '',
    altimeter: ''
  })

  const [links, setLinks] = useState({
    statistics: [] as string[],
    extremeValues: [] as string[],
    persistence: [] as string[],
    histogram: [] as string[],
    exceedance: [] as string[],
    tidal: [] as string[],
    altimeter: [] as string[]
  })

  const [errors, setErrors] = useState({
    statistics: '',
    extremeValues: '',
    persistence: '',
    histogram: '',
    exceedance: '',
    tidal: '',
    altimeter: ''
  })
  const [advancedAnalyticType, setAdvancedAnalyticType] = useState<ADVANCED_ANALYTICS>(ADVANCED_ANALYTICS.STATISTICS);

  const getAdvancedAnalyticsMutation = useGetAdvancedAnalyticsMutation();
  const abortControllerRef = useRef<AbortController | null>(null)

  const getAnalyticsKey = (type: ADVANCED_ANALYTICS) => {
    switch (type) {
      case ADVANCED_ANALYTICS.STATISTICS: return 'statistics'
      case ADVANCED_ANALYTICS.EXTREME_VALUES: return 'extremeValues'
      case ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW: return 'persistence'
      case ADVANCED_ANALYTICS.HISTOGRAM: return 'histogram'
      case ADVANCED_ANALYTICS.EXCEEDANCE: return 'exceedance'
      case ADVANCED_ANALYTICS.TIDAL_LEVEL: return 'tidal'
      case ADVANCED_ANALYTICS.ALTIMETER_COMPARISON: return 'altimeter'
      default: return 'statistics'
    }
  }

  const mutateAdvancedAnalytics = useCallback(
    async (payload: IAdvancedAnalyticsApiPayload, type: ADVANCED_ANALYTICS) => {
      setAdvancedAnalyticType(type)

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const controller = new AbortController()
      abortControllerRef.current = controller

      const key = getAnalyticsKey(type)

      try {
        const pointName = isFwe ? fwePointName ?? payload.PointName : payload.PointName

        const result = await getAdvancedAnalyticsMutation.mutateAsync({
          payload: {
            ...payload,
            PointName: pointName,
            PlotFlag: type === ADVANCED_ANALYTICS.HISTOGRAM ? '2' : '[1 3]',
          },
          signal: controller.signal,
        })
        if (result?.Result?.img) {
          const imageData = 'data:image/png;base64,' + result.Result.img

          setImages(prev => ({ ...prev, [key]: imageData }))
          setLinks(prev => ({ ...prev, [key]: result.Result?.links || [] }))
          setErrors(prev => ({ ...prev, [key]: '' }))

          trackUpdate(EVENT_ANALYTICS_TYPE_UPDATED, coordinates, payload.DatasetId, type)
        } else {
          const isCmems = payload.DatasetId.toLowerCase().includes('cmems') || payload.DatasetId2?.toLowerCase().includes('cmems')
          const errorMessage = isCmems ? 'Cmems error: Layer not available' : DEFAULT_ERROR_MESSAGE
          setImages(prev => ({ ...prev, [key]: '' }))          
          setLinks(prev => ({ ...prev, [key]: [] }))
          setErrors(prev => ({ ...prev, [key]: errorMessage }))
        }
      } catch (error: any) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
          setImages(prev => ({ ...prev, [key]: '' }))
          setLinks(prev => ({ ...prev, [key]: [] }))
          return
        }

        const errorMessage = error?.response?.data?.Message || DEFAULT_ERROR_MESSAGE
        setErrors(prev => ({ ...prev, [key]: errorMessage }))
        setImages(prev => ({ ...prev, [key]: '' }))
        setLinks(prev => ({ ...prev, [key]: [] }))
      }
    },
    [getAdvancedAnalyticsMutation, coordinates, isFwe, fwePointName]
  )

  const updateStatistics = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics(payload, ADVANCED_ANALYTICS.STATISTICS)
  }, [mutateAdvancedAnalytics])

  const updateExtremeValues = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics( payload, ADVANCED_ANALYTICS.EXTREME_VALUES)
  }, [mutateAdvancedAnalytics])

  const updatePersistence = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics( payload, ADVANCED_ANALYTICS.PERSISTENCE_WEATHERWINDOW)
  }, [mutateAdvancedAnalytics])

  const updateHistogram = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics( payload, ADVANCED_ANALYTICS.HISTOGRAM)
  }, [mutateAdvancedAnalytics])

  const updateExceedance = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics( payload, ADVANCED_ANALYTICS.EXCEEDANCE)
  }, [mutateAdvancedAnalytics])

  const updateTidalLevel = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics( payload, ADVANCED_ANALYTICS.TIDAL_LEVEL)
  }, [mutateAdvancedAnalytics])

  const updateAltimeterComparison = useCallback(async (payload: IAdvancedAnalyticsApiPayload) => {
    await mutateAdvancedAnalytics(payload, ADVANCED_ANALYTICS.ALTIMETER_COMPARISON)
  }, [mutateAdvancedAnalytics])

  const setErrorMessage = useCallback((type: ADVANCED_ANALYTICS, errorMessage: string) => {
    const key = getAnalyticsKey(type)
    setImages(prev => ({ ...prev, [key]: '' }))
    setErrors(prev => ({ ...prev, [key]: errorMessage }))
    setLinks(prev => ({ ...prev, [key]: [] }))
  }, [])

  const cancelAdvancedAnalytics = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  const resetAdvancedAnalytics = useCallback(() => {
    setImages({
      statistics: '',
      extremeValues: '',
      persistence: '',
      histogram: '',
      exceedance: '',
      tidal: '',
      altimeter: ''
    })
    setLinks({
      statistics: [],
      extremeValues: [],
      persistence: [],
      histogram: [],
      exceedance: [],
      tidal: [],
      altimeter: []
    })
    setErrors({
      statistics: '',
      extremeValues: '',
      persistence: '',
      histogram: '',
      exceedance: '',
      tidal: '',
      altimeter: ''
    })
  }, [])

  return {
    images,
    links,
    errors,
    updateStatistics,
    updateExtremeValues,
    updatePersistence,
    updateHistogram,
    updateExceedance,
    updateTidalLevel,
    updateAltimeterComparison,
    resetAdvancedAnalytics,
    advancedAnalyticType,
    setErrorMessage,
    cancelAdvancedAnalytics,
    isUpdating: getAdvancedAnalyticsMutation.isPending,
    mutateAdvancedAnalytics
  }
}
